/**
 * Design-system class tokens.
 *
 * Every color here is a semantic Tailwind class (bg-surface, text-muted, ...)
 * that resolves to CSS variables in index.css, so tokens are theme-agnostic:
 * light/dark is handled by the `dark` class on <html>, never by JS branching.
 *
 * Prefer composing these over writing ad-hoc color classes in components.
 */

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export const CONTENT_WIDTH = 'w-full max-w-4xl mx-auto min-w-0';
export const NARROW_WIDTH = 'w-full max-w-3xl mx-auto min-w-0';
export const SHARE_PAGE_WIDTH = 'w-full max-w-tool mx-auto min-w-0';

/** Vertical rhythm for a page: top offset + generous bottom. */
export const PAGE_STACK = 'pt-6 sm:pt-10 pb-16 sm:pb-24';

export const SECTION_PAD = 'p-5 sm:p-6';
export const SECTION_PAD_LG = 'p-6 sm:p-8';

// ---------------------------------------------------------------------------
// Product options
// ---------------------------------------------------------------------------

export const EXPIRY_OPTIONS = [
  { label: '5 minutes', value: '5m' },
  { label: '1 hour', value: '1h' },
  { label: '24 hours', value: '1d' },
  { label: '7 days', value: '7d' },
];

export function expiryDisplayLabel(wrapTtl) {
  const match = EXPIRY_OPTIONS.find((option) => option.value === wrapTtl);
  return match ? match.label : wrapTtl;
}

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------

export const TEXT = {
  display: 'text-display font-semibold text-fg',
  h1: 'text-h1 font-semibold text-fg',
  h2: 'text-h2 font-semibold text-fg',
  h3: 'text-h3 font-semibold text-fg',
  eyebrow: 'text-eyebrow font-semibold uppercase text-muted',
  eyebrowAccent: 'text-eyebrow font-semibold uppercase text-accent',
  lead: 'text-[17px] sm:text-lg leading-relaxed text-muted',
  body: 'text-[15px] leading-relaxed text-muted',
  bodyStrong: 'text-[15px] leading-relaxed text-fg',
  small: 'text-sm text-muted',
  caption: 'text-xs text-muted',
  label: 'block text-sm font-medium text-fg',
  link: 'text-accent-text hover:text-accent-hover underline underline-offset-[3px] decoration-accent/40 hover:decoration-accent transition-colors',
};

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

/** Standard card: white/dark surface with a hairline edge. */
export const SURFACE = 'rounded-card bg-surface shadow-e1';
/** Elevated card for a confirmed / focal state. */
export const SURFACE_RAISED = 'rounded-card bg-surface shadow-e2';
/** Recessed panel inside a card (secret preview, per-link rows). */
export const INSET = 'rounded-lg border border-line bg-surface-2';
/** Terminal-style block for shell commands, dark in both themes. */
export const CODE_BLOCK = 'rounded-lg bg-terminal text-terminal-fg font-mono text-[13px] leading-relaxed shadow-e1';

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

const BUTTON_BASE = 'inline-flex items-center justify-center gap-2 rounded-lg font-medium whitespace-nowrap select-none transition-colors duration-150 disabled:cursor-not-allowed';

const BUTTON_SIZE = {
  sm: 'h-9 px-3 text-[13px]',
  md: 'min-h-11 px-4 text-sm',
  lg: 'min-h-[46px] px-5 text-[15px]',
  icon: 'h-9 w-9 p-0',
};

const BUTTON_VARIANT = {
  primary: 'bg-accent text-accent-fg hover:bg-accent-hover active:bg-accent-active disabled:bg-disabled-bg disabled:text-disabled-fg',
  secondary: 'border border-line bg-surface text-fg hover:bg-surface-2 hover:border-line-strong active:bg-canvas disabled:opacity-50',
  outline: 'border border-accent text-accent bg-transparent hover:bg-accent/10 active:bg-accent/20 disabled:border-line disabled:text-disabled-fg disabled:bg-transparent',
  ghost: 'text-accent-text hover:bg-accent/10 active:bg-accent/15 disabled:opacity-50',
  quiet: 'text-muted hover:text-fg hover:bg-fg/5 active:bg-fg/10 disabled:opacity-50',
};

export function buttonClass({ variant = 'primary', size = 'md', block = false } = {}) {
  return [
    BUTTON_BASE,
    BUTTON_SIZE[size] || BUTTON_SIZE.md,
    BUTTON_VARIANT[variant] || BUTTON_VARIANT.primary,
    block ? 'w-full' : '',
  ].filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Form controls
// ---------------------------------------------------------------------------

const INPUT_BASE = 'w-full min-w-0 min-h-11 px-3.5 py-2.5 rounded-lg border bg-surface text-fg text-base sm:text-sm placeholder:text-subtle caret-accent transition-colors disabled:opacity-50';

export function inputClass(invalid = false) {
  const state = invalid
    ? 'border-danger focus:border-danger'
    : 'border-line hover:border-line-strong focus:border-accent';
  return `${INPUT_BASE} ${state}`;
}

/** Large writing surface: flush on the card in light, recessed in dark. */
export const TEXTAREA = 'w-full min-h-[148px] px-3.5 py-3 rounded-lg border border-line bg-surface dark:bg-canvas text-fg text-[15px] leading-normal placeholder:text-subtle caret-accent resize-none hover:border-line-strong focus:border-accent transition-colors';

/** Read-only monospace field for share links. */
export const MONO_FIELD = 'flex-1 min-w-0 min-h-11 px-3.5 py-2.5 rounded-lg border border-line bg-surface-2 font-mono text-[13px] text-accent-text focus:border-accent';

export const SEGMENT_WRAP = 'inline-flex flex-wrap rounded-lg border border-line overflow-hidden bg-surface';

export function segmentOptionClass(selected) {
  const base = 'relative px-3 py-[7px] text-[13px] cursor-pointer transition-colors has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-accent';
  return selected
    ? `${base} text-accent font-medium ring-1 ring-inset ring-accent bg-accent/[0.06]`
    : `${base} text-fg hover:bg-fg/5`;
}

export const SWITCH_HIT = 'relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl hover:bg-fg/5';

export function switchTrackClass(on) {
  return `relative inline-flex h-6 w-[42px] items-center rounded-full transition-colors duration-200 ${on ? 'bg-accent' : 'bg-control'}`;
}

export function switchThumbClass(on) {
  return `inline-block h-[18px] w-[18px] rounded-full bg-white shadow-sm transition-transform duration-200 ease-out ${on ? 'translate-x-[21px]' : 'translate-x-[3px]'}`;
}

// ---------------------------------------------------------------------------
// Tags, alerts, misc
// ---------------------------------------------------------------------------

export const TAG_OUTLINE = 'inline-flex items-center gap-1.5 rounded-md border border-accent/70 px-2.5 py-[3px] text-[11px] font-medium leading-none text-accent';
export const TAG_ACCENT = 'inline-flex items-center rounded-md bg-accent-soft px-2.5 py-1 text-xs font-medium text-accent-soft-fg';
export const TAG_NEUTRAL = 'inline-flex items-center rounded-md bg-surface-2 border border-line px-2.5 py-1 text-xs font-medium text-muted';

export const ALERT_ERROR = 'flex items-start gap-3 rounded-lg border border-danger/25 bg-danger-soft px-4 py-3 text-sm text-danger';
export const ALERT_SUCCESS = 'flex items-start gap-3 rounded-lg border border-success/25 bg-success-soft px-4 py-3 text-sm text-success';

export const DIVIDER = 'border-line';

export const ICON_TILE = 'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent-soft-fg';

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

export function navLinkClass(active) {
  const base = 'relative inline-flex h-14 items-center text-sm font-medium whitespace-nowrap transition-colors after:absolute after:inset-x-0 after:bottom-0 after:h-[2px] after:rounded-full after:bg-accent after:transition-opacity';
  return active
    ? `${base} text-fg after:opacity-100`
    : `${base} text-muted hover:text-fg after:opacity-0`;
}

export function mobileNavItemClass(active) {
  const base = 'w-full text-left min-h-11 px-3 rounded-lg text-[15px] font-medium transition-colors';
  return active
    ? `${base} bg-accent/10 text-accent-text`
    : `${base} text-fg hover:bg-fg/5`;
}

export const FOOTER_LINK = 'text-[13px] text-muted hover:text-fg transition-colors';
